import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Space,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Upload,
  Select,
  Divider,
  Tabs,
  Timeline,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  SettingOutlined,
  HistoryOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { inventoryAmountApi } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const InventoryAmount = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('config');
  
  // 文件处理相关状态
  const [processing, setProcessing] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  // 批量处理：支持多个文件组合
  const [fileGroups, setFileGroups] = useState([
    { id: 1, inventoryFile: null, amountFile: null, cumulativeFile: null }
  ]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 获取配置列表
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await inventoryAmountApi.getConfigs();
      setConfigs(response.items || []);
    } catch (error) {
      message.error('获取配置失败');
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取处理历史
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await inventoryAmountApi.getHistory();
      setHistory(response.data?.histories || []);
    } catch (error) {
      message.error('获取处理历史失败');
      console.error('获取处理历史失败:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'config') {
      fetchConfigs();
    } else if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  // 创建或更新配置
  const handleSubmit = async (values) => {
    try {
      // 构建倍数配置
      const multipliers = {};
      if (values.region === '美国') {
        multipliers.price_multiplier = values.price_multiplier;
        multipliers.gt_300_multiplier = values.gt_300_multiplier;
        multipliers.gt_600_multiplier = values.gt_600_multiplier;
        multipliers.gt_900_multiplier = values.gt_900_multiplier;
        multipliers.gt_1200_multiplier = values.gt_1200_multiplier;
        multipliers.gt_1500_multiplier = values.gt_1500_multiplier;
      } else {
        multipliers.price_multiplier = values.price_multiplier;
        multipliers.gt_300_multiplier = values.gt_300_multiplier;
        // 小于阈值可配置（默认30，兼容旧配置）
        multipliers.lt_threshold = values.lt_threshold != null ? values.lt_threshold : 30;
        multipliers.lt_30_multiplier = values.lt_30_multiplier;
      }

      const configData = {
        name: values.name,
        region: values.region,
        description: values.description,
        multipliers: multipliers,
        is_active: values.is_active !== false,
        output_business_price: values.output_business_price === true,
      };

      if (editingConfig) {
        await inventoryAmountApi.updateConfig(editingConfig.id, configData);
        message.success('配置更新成功');
      } else {
        await inventoryAmountApi.createConfig(configData);
        message.success('配置创建成功');
      }
      setModalVisible(false);
      setEditingConfig(null);
      form.resetFields();
      fetchConfigs();
    } catch (error) {
      message.error(editingConfig ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 删除配置
  const handleDelete = async (id) => {
    try {
      await inventoryAmountApi.deleteConfig(id);
      message.success('删除成功');
      fetchConfigs();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 激活配置
  const handleActivate = async (id) => {
    try {
      await inventoryAmountApi.activateConfig(id);
      message.success('激活成功');
      fetchConfigs();
    } catch (error) {
      message.error('激活失败');
      console.error('激活失败:', error);
    }
  };

  // 打开编辑模态框
  const showEditModal = (config) => {
    setEditingConfig(config);
    const formValues = {
      ...config,
      ...config.multipliers,
      lt_threshold: config.multipliers && config.multipliers.lt_threshold != null ? config.multipliers.lt_threshold : 30,
      output_business_price: !!config.output_business_price,
    };
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  // 打开创建模态框
  const showCreateModal = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ region: '美国', is_active: true, output_business_price: true });
    setModalVisible(true);
  };

  // 添加新的文件组合
  const addFileGroup = () => {
    const newId = Math.max(...fileGroups.map(g => g.id), 0) + 1;
    setFileGroups([...fileGroups, {
      id: newId,
      inventoryFile: null,
      amountFile: null,
      cumulativeFile: null
    }]);
  };

  // 删除文件组合
  const removeFileGroup = (groupId) => {
    if (fileGroups.length === 1) {
      message.warning('至少需要保留一个文件组合');
      return;
    }
    setFileGroups(fileGroups.filter(g => g.id !== groupId));
  };

  // 更新文件组合中的文件
  const updateFileGroup = (groupId, fileType, file) => {
    setFileGroups(fileGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, [fileType]: file };
      }
      return group;
    }));
  };

  // 批量处理文件上传（串行处理，避免浏览器并发限制）
  const handleProcess = async () => {
    if (!selectedConfigId) {
      message.error('请选择配置');
      return;
    }

    // 验证所有文件组合是否完整
    const incompleteGroups = fileGroups.filter(group => 
      !group.inventoryFile || !group.amountFile || !group.cumulativeFile
    );

    if (incompleteGroups.length > 0) {
      message.error(`有 ${incompleteGroups.length} 个文件组合未完整，请检查`);
      return;
    }

    setProcessing(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      // 串行处理所有文件组合，避免浏览器并发连接限制
      for (let i = 0; i < fileGroups.length; i++) {
        const group = fileGroups[i];
        const groupIndex = i + 1;
        
        try {
          message.loading({
            content: `正在处理第 ${groupIndex}/${fileGroups.length} 个文件组合...`,
            key: 'processing',
            duration: 0
          });

          const formData = new FormData();
          formData.append('inventory_file', group.inventoryFile);
          formData.append('amount_file', group.amountFile);
          formData.append('cumulative_file', group.cumulativeFile);
          formData.append('config_id', selectedConfigId);

          // 记录文件信息用于调试
          console.log(`开始处理文件组合 ${groupIndex}:`, {
            inventoryFile: group.inventoryFile?.name,
            amountFile: group.amountFile?.name,
            cumulativeFile: group.cumulativeFile?.name,
            configId: selectedConfigId
          });

          await inventoryAmountApi.processFiles(formData);
          successCount++;
          
          console.log(`文件组合 ${groupIndex} 处理成功`);
        } catch (error) {
          failCount++;
          
          // 详细的错误信息提取
          let errorMsg = '未知错误';
          if (error.response) {
            // 服务器返回了响应
            errorMsg = error.response.data?.message || 
                      error.response.data?.detail || 
                      `HTTP ${error.response.status}: ${error.response.statusText}`;
            console.error(`文件组合 ${groupIndex} 服务器错误:`, {
              status: error.response.status,
              data: error.response.data,
              headers: error.response.headers
            });
          } else if (error.request) {
            // 请求已发出但没有收到响应
            errorMsg = '网络错误：请求未到达服务器，请检查网络连接';
            console.error(`文件组合 ${groupIndex} 网络错误:`, {
              request: error.request,
              message: error.message
            });
          } else {
            // 其他错误
            errorMsg = error.message || '未知错误';
            console.error(`文件组合 ${groupIndex} 其他错误:`, error);
          }
          
          errors.push(`文件组合 ${groupIndex}: ${errorMsg}`);
        }
      }

      // 显示处理结果
      message.destroy('processing');
      
      if (successCount > 0) {
        message.success(`成功提交 ${successCount} 个文件组合进行处理`);
      }
      
      if (failCount > 0) {
        const errorDetails = errors.join('\n');
        message.error({
          content: `${failCount} 个文件组合处理失败\n${errorDetails}`,
          duration: 10
        });
      }
      
      // 清空所有文件组合，重置为初始状态
      setFileGroups([{
        id: 1,
        inventoryFile: null,
        amountFile: null,
        cumulativeFile: null
      }]);
      
      // 刷新历史记录
      fetchHistory();
      setActiveTab('history');
    } catch (error) {
      message.destroy('processing');
      message.error('批量处理失败');
      console.error('批量处理失败:', error);
    } finally {
      setProcessing(false);
    }
  };

  // 下载结果（TXT或Excel）
  const handleDownload = async (sessionId, fileType = 'txt') => {
    try {
      message.loading({ content: '正在下载文件...', key: 'download' });
      
      const response = await inventoryAmountApi.downloadResult(sessionId, fileType);
      
      // 从响应中获取blob（响应拦截器返回的是完整response对象）
      const blob = response.data || response;
      
      // 检查blob是否有效
      if (!blob) {
        throw new Error('响应数据为空');
      }
      
      // 如果返回的是错误信息（JSON格式），尝试解析
      if (blob instanceof Blob && blob.type === 'application/json') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || errorData.message || '下载失败');
        } catch (parseError) {
          throw new Error('服务器返回错误信息');
        }
      }
      
      // 确保是Blob对象
      if (!(blob instanceof Blob)) {
        throw new Error('无效的响应数据格式');
      }
      
      // 从响应头获取文件名（如果有）
      let downloadFilename = `inventory_amount_result_${sessionId}.${fileType === 'excel' ? 'xlsx' : 'txt'}`;
      
      // 尝试多种方式获取 headers
      const headers = response.headers || response.response?.headers || {};
      
      // 尝试获取 Content-Disposition（不区分大小写）
      let disposition = null;
      for (const key in headers) {
        if (key.toLowerCase() === 'content-disposition') {
          disposition = headers[key];
          break;
        }
      }
      
      // 如果还是找不到，尝试直接访问
      if (!disposition) {
        disposition = headers['content-disposition'] || headers['Content-Disposition'];
      }
      
      console.log('下载响应头:', headers);
      console.log('Content-Disposition:', disposition);
      
      if (disposition) {
        // 优先尝试提取UTF-8编码的文件名（filename*=UTF-8''...）
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match && utf8Match[1]) {
          try {
            downloadFilename = decodeURIComponent(utf8Match[1].trim());
            console.log('提取UTF-8文件名:', downloadFilename);
          } catch (e) {
            console.warn('UTF-8文件名解码失败:', e);
            // 如果解码失败，尝试提取普通文件名
            const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              downloadFilename = filenameMatch[1].replace(/['"]/g, '').trim();
            }
          }
        } else {
          // 如果没有UTF-8编码的文件名，尝试提取普通文件名
          const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1].replace(/['"]/g, '').trim();
            console.log('提取普通文件名:', downloadFilename);
          }
        }
      } else {
        console.warn('未找到 Content-Disposition 头，使用默认文件名');
      }
      
      console.log('最终下载文件名:', downloadFilename);
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      message.success({ content: `下载${fileType === 'excel' ? 'Excel' : 'TXT'}成功`, key: 'download' });
    } catch (error) {
      message.error({ content: `下载失败: ${error.message || '未知错误'}`, key: 'download' });
      console.error('下载失败:', error);
    }
  };

  const configColumns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
      render: (text) => <Tag color={text === '美国' ? 'blue' : 'green'}>{text}</Tag>,
    },
    {
      title: '倍数配置',
      dataIndex: 'multipliers',
      key: 'multipliers',
      render: (multipliers) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {multipliers ? Object.keys(multipliers).length + ' 项' : '0 项'}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '激活' : '未激活'}
        </Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.is_active && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleActivate(record.id)}
            >
              激活
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个配置吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: '会话ID',
      dataIndex: 'session_id',
      key: 'session_id',
      render: (text) => <Text code style={{ fontSize: '12px' }}>{text?.substring(0, 8)}...</Text>,
    },
    {
      title: '地区',
      dataIndex: 'region',
      key: 'region',
      render: (text) => <Tag color={text === '美国' ? 'blue' : 'green'}>{text}</Tag>,
    },
    {
      title: '文件',
      key: 'files',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: '12px' }}>
            库存: {record.inventory_file_name}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            金额: {record.amount_file_name}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            累计: {record.cumulative_file_name}
          </Text>
        </Space>
      ),
    },
    {
      title: '处理状态',
      key: 'status',
      render: (_, record) => {
        const status = record.status || 'processing';
        let statusColor = 'default';
        let statusText = '处理中';
        
        if (status === 'success') {
          statusColor = 'success';
          statusText = '已完成';
        } else if (status === 'failed') {
          statusColor = 'error';
          statusText = '失败';
        }
        
        return (
          <Space direction="vertical" size="small">
            <Tag color={statusColor}>{statusText}</Tag>
            {record.success_rate !== undefined && status === 'success' && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                成功率: {(record.success_rate * 100).toFixed(1)}%
              </Text>
            )}
            {record.error_message && (
              <Text type="danger" style={{ fontSize: '12px' }} ellipsis={{ tooltip: record.error_message }}>
                错误: {record.error_message}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => {
        const status = record.status || 'processing';
        const isSuccess = status === 'success';
        const isFailed = status === 'failed';
        
        return (
          <Space>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              disabled={!isSuccess || !record.has_excel_file}
              onClick={() => handleDownload(record.session_id, 'excel')}
              size="small"
            >
              Excel
            </Button>
            {isFailed && record.error_message && (
              <Text type="danger" style={{ fontSize: '12px' }} ellipsis={{ tooltip: record.error_message }}>
                错误
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={3}>库存金额处理（新）</Title>
        
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={<span><SettingOutlined />配置管理</span>}
            key="config"
          >
            <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
              <Col>
                <Text type="secondary">
                  美国站请选「美国」配置；欧洲各站（英、德、法、意、西、荷、波、北欧、东欧等）请选「欧洲」配置。文件名可带国家/地区名便于核对。
                </Text>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={showCreateModal}
                >
                  新建配置
                </Button>
              </Col>
            </Row>

            <Table
              columns={configColumns}
              dataSource={configs}
              loading={loading}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
            />
          </TabPane>

          <TabPane
            tab={<span><FileTextOutlined />文件处理</span>}
            key="process"
          >
            <Card>
              <Form layout="vertical">
                <Form.Item label="选择配置" required>
                  <Select
                    placeholder="请选择配置"
                    value={selectedConfigId}
                    onChange={setSelectedConfigId}
                    style={{ width: '100%' }}
                  >
                    {configs.filter(c => c.is_active).map(config => (
                      <Option key={config.id} value={config.id}>
                        {config.name} ({config.region})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Divider>批量上传文件（可添加多个文件组合）</Divider>

                {fileGroups.map((group, index) => (
                  <Card
                    key={group.id}
                    title={`文件组合 ${index + 1}`}
                    extra={
                      fileGroups.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => removeFileGroup(group.id)}
                        >
                          删除
                        </Button>
                      )
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Form.Item label="库存模板" required>
                      <Upload
                        beforeUpload={(file) => {
                          updateFileGroup(group.id, 'inventoryFile', file);
                          return false;
                        }}
                        onRemove={() => updateFileGroup(group.id, 'inventoryFile', null)}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>选择文件</Button>
                      </Upload>
                      {group.inventoryFile && (
                        <Text type="success" style={{ marginLeft: 8 }}>{group.inventoryFile.name}</Text>
                      )}
                    </Form.Item>

                    <Form.Item label="库存金额模板" required>
                      <Upload
                        beforeUpload={(file) => {
                          updateFileGroup(group.id, 'amountFile', file);
                          return false;
                        }}
                        onRemove={() => updateFileGroup(group.id, 'amountFile', null)}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>选择文件</Button>
                      </Upload>
                      {group.amountFile && (
                        <Text type="success" style={{ marginLeft: 8 }}>{group.amountFile.name}</Text>
                      )}
                    </Form.Item>

                    <Form.Item label="累计计算模板" required>
                      <Upload
                        beforeUpload={(file) => {
                          updateFileGroup(group.id, 'cumulativeFile', file);
                          return false;
                        }}
                        onRemove={() => updateFileGroup(group.id, 'cumulativeFile', null)}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>选择文件</Button>
                      </Upload>
                      {group.cumulativeFile && (
                        <Text type="success" style={{ marginLeft: 8 }}>{group.cumulativeFile.name}</Text>
                      )}
                    </Form.Item>
                  </Card>
                ))}

                <Form.Item>
                  <Space>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={addFileGroup}
                    >
                      添加文件组合
                    </Button>
                    <Text type="secondary">
                      当前有 {fileGroups.length} 个文件组合
                    </Text>
                  </Space>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    icon={<UploadOutlined />}
                    loading={processing}
                    onClick={handleProcess}
                    disabled={
                      !selectedConfigId ||
                      fileGroups.some(group => !group.inventoryFile || !group.amountFile || !group.cumulativeFile)
                    }
                  >
                    批量开始处理 ({fileGroups.length} 个文件组合)
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </TabPane>

          <TabPane
            tab={<span><HistoryOutlined />处理历史</span>}
            key="history"
          >
            <Table
              columns={historyColumns}
              dataSource={history}
              loading={historyLoading}
              rowKey="session_id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
            />
          </TabPane>
        </Tabs>

        {/* 配置编辑模态框 */}
        <Modal
          title={editingConfig ? '编辑配置' : '新建配置'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingConfig(null);
            form.resetFields();
          }}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              region: '美国',
              is_active: true,
              price_multiplier: 1.0,
              gt_300_multiplier: 1.0,
              gt_600_multiplier: 1.0,
              gt_900_multiplier: 1.0,
              gt_1200_multiplier: 1.0,
              gt_1500_multiplier: 1.0,
              lt_30_multiplier: 1.0,
              lt_threshold: 30,
            }}
          >
            <Form.Item
              name="name"
              label="配置名称"
              rules={[{ required: true, message: '请输入配置名称' }]}
            >
              <Input placeholder="请输入配置名称" />
            </Form.Item>

            <Form.Item
              name="region"
              label="地区"
              rules={[{ required: true, message: '请选择地区' }]}
            >
              <Select placeholder="请选择地区">
                <Option value="美国">美国</Option>
                <Option value="欧洲">欧洲（各欧盟/欧洲亚马逊站点）</Option>
              </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.region !== currentValues.region}>
              {({ getFieldValue }) => {
                const region = getFieldValue('region');
                return region === '美国' ? (
                  <>
                    <Form.Item
                      name="price_multiplier"
                      label="售价倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_300_multiplier"
                      label="大于300倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_600_multiplier"
                      label="大于600倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_900_multiplier"
                      label="大于900倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_1200_multiplier"
                      label="大于1200倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_1500_multiplier"
                      label="大于1500倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <Form.Item
                      name="price_multiplier"
                      label="售价倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="gt_300_multiplier"
                      label="大于300倍数"
                      rules={[{ required: true, message: '请输入倍数' }]}
                    >
                      <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="lt_threshold"
                      label="小于阈值"
                      tooltip="B2B价格低于该阈值时使用下方“小于倍数”；不填默认30"
                      rules={[{ required: true, message: '请输入阈值' }]}
                    >
                      <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.lt_threshold !== currentValues.lt_threshold}
                    >
                      {({ getFieldValue }) => (
                        <Form.Item
                          name="lt_30_multiplier"
                          label={`小于${getFieldValue('lt_threshold') || 30}倍数`}
                          rules={[{ required: true, message: '请输入倍数' }]}
                        >
                          <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>

            <Form.Item name="description" label="描述">
              <Input.TextArea rows={3} placeholder="请输入配置描述" />
            </Form.Item>

            <Form.Item name="is_active" label="是否激活" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item name="output_business_price" label="输出企业价格" valuePropName="checked" tooltip="企业价格 = 售价 × 0.9">
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingConfig ? '更新' : '创建'}
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    setEditingConfig(null);
                    form.resetFields();
                  }}
                >
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default InventoryAmount;




